var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS from 'api/queries/GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS';
import InfoIcon from 'images/info-circle-red.svg';
import PlanSvg from 'images/v5/icons/file-check-01.svg';
import Button from 'layout/atoms/Button/Button';
import CalendarRange from 'layout/atoms/CalendarRange/CalendarRange';
import Header from 'layout/atoms/Header/Header';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import Tooltip from 'layout/atoms/Tooltip';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { approveOrderSpecificationProduction, revertOrderSpecificationProductionApproval, selectOrderProductionManagerStatus, } from 'store/orderProductionManagerSlice';
import { addToast } from 'store/toastsSlice';
import toUtcDateOnlyISOString from 'utils/toUtcDateOnlyISOString';
import styles from './ProductionApprovalDetails.module.scss';
function ProductionApprovalDetails() {
    var _a, _b, _c, _d, _e;
    const apolloClient = useApolloClient();
    const { t, i18n } = useTranslation(['orders', 'common']);
    const dispatch = useDispatch();
    const modalData = (_a = useSelector(selectModalByName('productionApprovalDetails'))) === null || _a === void 0 ? void 0 : _a.data;
    const status = useSelector(selectOrderProductionManagerStatus);
    const isPreviewMode = (modalData === null || modalData === void 0 ? void 0 : modalData.mode) === 'preview';
    const specificationName = (modalData === null || modalData === void 0 ? void 0 : modalData.specificationName) || '-';
    const manufacturerName = (modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerName) || '-';
    const manufacturerEmployees = (_b = modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerEmployees) !== null && _b !== void 0 ? _b : 0;
    const manufacturerNonWorkingDays = useMemo(() => (modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerNonWorkingDays) || [], [modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerNonWorkingDays]);
    const [occupiedOrderIdsByDate, setOccupiedOrderIdsByDate] = useState({});
    const [occupiedOrdersByDate, setOccupiedOrdersByDate] = useState({});
    const latestCalendarRequestRef = useRef('');
    const manufacturerCapacity = manufacturerEmployees * 480;
    const productionTimeMinutes = (_c = modalData === null || modalData === void 0 ? void 0 : modalData.productionTimeMinutes) !== null && _c !== void 0 ? _c : '-';
    const sumOfModelQuantities = (_d = modalData === null || modalData === void 0 ? void 0 : modalData.sumOfModelQuantities) !== null && _d !== void 0 ? _d : '-';
    const totalProductionTimeMinutes = (_e = modalData === null || modalData === void 0 ? void 0 : modalData.totalProductionTimeMinutes) !== null && _e !== void 0 ? _e : null;
    const productionStartDate = (modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate)
        ? moment(modalData.productionStartDate).format('DD.MM.YYYY')
        : '-';
    const endDate = (modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate)
        ? moment(modalData.virtualEndDate).format('DD.MM.YYYY')
        : '-';
    const calendarInitialMonth = useMemo(() => {
        if (modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate) {
            return moment(modalData.productionStartDate).startOf('day');
        }
        if (modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate) {
            return moment(modalData.virtualEndDate).startOf('day');
        }
        return moment().startOf('day');
    }, [modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate, modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate]);
    const disabledDates = useMemo(() => {
        if (!manufacturerNonWorkingDays.length) {
            return [];
        }
        const dates = [];
        manufacturerNonWorkingDays.forEach((range) => {
            if (!(range === null || range === void 0 ? void 0 : range.startDate) || !(range === null || range === void 0 ? void 0 : range.endDate)) {
                return;
            }
            const start = moment(range.startDate).startOf('day');
            const end = moment(range.endDate).startOf('day');
            const cursor = start.clone();
            while (cursor.isSameOrBefore(end, 'day')) {
                dates.push(cursor.clone());
                cursor.add(1, 'day');
            }
        });
        return dates;
    }, [manufacturerNonWorkingDays]);
    const occupiedDates = useMemo(() => Object.keys(occupiedOrderIdsByDate).map((date) => moment(date).startOf('day')), [occupiedOrderIdsByDate]);
    const productionDates = useMemo(() => {
        if (!isPreviewMode || !(modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate) || !(modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate)) {
            return [];
        }
        const start = moment(modalData.productionStartDate).startOf('day');
        const end = moment(modalData.virtualEndDate).startOf('day');
        if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
            return [];
        }
        const nonWorkingDateKeys = new Set(disabledDates.map((date) => date.format('YYYY-MM-DD')));
        const dates = [];
        const cursor = start.clone();
        while (cursor.isSameOrBefore(end, 'day')) {
            const dayOfWeek = cursor.isoWeekday();
            const dateKey = cursor.format('YYYY-MM-DD');
            if (dayOfWeek < 6 && !nonWorkingDateKeys.has(dateKey)) {
                dates.push(cursor.clone());
            }
            cursor.add(1, 'day');
        }
        return dates;
    }, [disabledDates, isPreviewMode, modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate, modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate]);
    const totalProductionTimeDaysLabel = useMemo(() => {
        if (totalProductionTimeMinutes === null || totalProductionTimeMinutes <= 0) {
            return null;
        }
        const days = totalProductionTimeMinutes / 480;
        const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(days);
    }, [i18n.language, totalProductionTimeMinutes]);
    const manufacturerCapacityDaysLabel = useMemo(() => {
        if (!manufacturerCapacity || manufacturerCapacity <= 0) {
            return null;
        }
        const days = manufacturerCapacity / 480;
        const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(days);
    }, [i18n.language, manufacturerCapacity]);
    const nonWorkingDaysInRange = (() => {
        if (!(modalData === null || modalData === void 0 ? void 0 : modalData.productionStartDate) || !(modalData === null || modalData === void 0 ? void 0 : modalData.virtualEndDate)) {
            return '-';
        }
        const rangeStart = moment(modalData.productionStartDate).startOf('day');
        const rangeEnd = moment(modalData.virtualEndDate).startOf('day');
        if (!rangeStart.isValid() || !rangeEnd.isValid()) {
            return '-';
        }
        const dates = new Set();
        manufacturerNonWorkingDays.forEach((range) => {
            if (!(range === null || range === void 0 ? void 0 : range.startDate) || !(range === null || range === void 0 ? void 0 : range.endDate)) {
                return;
            }
            const start = moment(range.startDate).startOf('day');
            const end = moment(range.endDate).startOf('day');
            if (!start.isValid() || !end.isValid()) {
                return;
            }
            const cursor = moment.max(start, rangeStart).clone();
            const last = moment.min(end, rangeEnd);
            while (cursor.isSameOrBefore(last, 'day')) {
                dates.add(cursor.format('DD.MM.YYYY'));
                cursor.add(1, 'day');
            }
        });
        return dates.size ? Array.from(dates).join(', ') : '-';
    })();
    const handleCalendarVisibleRangeChange = useCallback((visibleStartDate, visibleEndDate) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!(modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerId)) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const startDate = toUtcDateOnlyISOString(visibleStartDate);
        const endDate = toUtcDateOnlyISOString(visibleEndDate);
        if (!startDate || !endDate) {
            setOccupiedOrderIdsByDate({});
            setOccupiedOrdersByDate({});
            return;
        }
        const requestKey = `${modalData.manufacturerId}:${startDate}:${endDate}`;
        latestCalendarRequestRef.current = requestKey;
        try {
            const response = yield apolloClient.query({
                query: GET_MANUFACTURER_PRODUCTION_CALENDAR_DAYS,
                variables: {
                    manufacturerId: modalData.manufacturerId,
                    startDate,
                    endDate,
                },
                fetchPolicy: 'network-only',
            });
            if (latestCalendarRequestRef.current !== requestKey) {
                return;
            }
            const calendarDays = ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b.productionCalendarDays) || [];
            const occupiedOrderIdsByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                day.orderIds || [],
            ]));
            const occupiedOrdersByDateMap = Object.fromEntries(calendarDays.map((day) => [
                moment(day.date).format('YYYY-MM-DD'),
                (day.orders || []).map((order) => ({
                    orderId: order === null || order === void 0 ? void 0 : order.orderId,
                    specificationNames: (order === null || order === void 0 ? void 0 : order.specificationNames) || [],
                })),
            ]));
            setOccupiedOrderIdsByDate(occupiedOrderIdsByDateMap);
            setOccupiedOrdersByDate(occupiedOrdersByDateMap);
        }
        catch (_c) {
            if (latestCalendarRequestRef.current === requestKey) {
                setOccupiedOrderIdsByDate({});
                setOccupiedOrdersByDate({});
            }
        }
    }), [apolloClient, modalData === null || modalData === void 0 ? void 0 : modalData.manufacturerId]);
    const handleApproveProduction = () => {
        if (!(modalData === null || modalData === void 0 ? void 0 : modalData.orderId) || !(modalData === null || modalData === void 0 ? void 0 : modalData.specificationId)) {
            return;
        }
        const approvalAction = approveOrderSpecificationProduction({
            orderId: modalData.orderId,
            specificationId: modalData.specificationId,
        });
        dispatch(approvalAction)
            .unwrap()
            .then(() => {
            dispatch(closeModal({ name: 'productionApprovalDetails' }));
        })
            .catch(() => {
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('orders:planning_production_approval_error_title'),
                description: t('orders:planning_production_approval_error_description'),
            }));
        });
    };
    const handleReturnToPlanningMode = () => {
        if (!(modalData === null || modalData === void 0 ? void 0 : modalData.orderId) || !(modalData === null || modalData === void 0 ? void 0 : modalData.specificationId)) {
            return;
        }
        const revertAction = revertOrderSpecificationProductionApproval({
            orderId: modalData.orderId,
            specificationId: modalData.specificationId,
        });
        dispatch(revertAction)
            .unwrap()
            .then(() => {
            dispatch(closeModal({ name: 'productionApprovalDetails' }));
        })
            .catch(() => {
            dispatch(addToast({
                theme: 'ghost',
                color: 'negative',
                title: t('orders:planning_revert_production_approval_error_title'),
                description: t('orders:planning_revert_production_approval_error_description'),
            }));
        });
    };
    return (React.createElement(Modal, { alwaysScroll: true, name: "productionApprovalDetails", size: "s", header: React.createElement(Label, { weight: "strong" },
            React.createElement(PlanSvg, null),
            ' ',
            isPreviewMode
                ? t('orders:planning_event_production_approved')
                : t('orders:planning_approve_production')) },
        React.createElement(Grid, { columns: 1, gap: 24 },
            React.createElement(Grid, { columns: 1, gap: 16 },
                React.createElement("div", null,
                    React.createElement(Header, { size: "s" }, t('orders:planning_specification_name')),
                    React.createElement(Grid, { columns: 1, gap: 12 },
                        React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_name'), value: specificationName }),
                        React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_production_model_qty'), value: `${sumOfModelQuantities} ${t('common:piece')}` }),
                        React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_production_time_minutes'), value: `${productionTimeMinutes} min = 1 ${t('common:piece')}` }),
                        React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_production_total_time_minutes'), value: totalProductionTimeMinutes !== null ? (React.createElement("span", { className: styles.valueWithHint },
                                React.createElement("span", null, `${totalProductionTimeMinutes} min`),
                                totalProductionTimeDaysLabel && (React.createElement(Tooltip, { theme: "inverse", content: t('orders:planning_production_total_time_days_tooltip', {
                                        days: totalProductionTimeDaysLabel,
                                    }), position: "top", delay: 200 },
                                    React.createElement("span", { className: styles.infoTrigger },
                                        React.createElement(InfoIcon, { className: styles.infoIcon })))))) : ('-') }))),
                React.createElement("div", null,
                    React.createElement(Header, { size: "s" }, t('orders:planning_manufacturer')),
                    React.createElement("div", { className: styles.manufacturerLayout },
                        React.createElement(Grid, { columns: 1, gap: 12 },
                            React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_name'), value: manufacturerName }),
                            React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_manufacturer_employees'), value: manufacturerEmployees }),
                            React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_manufacturer_capacity'), value: React.createElement("span", { className: styles.valueWithHint },
                                    React.createElement("span", null, `${manufacturerCapacity} min`),
                                    manufacturerCapacityDaysLabel && (React.createElement(Tooltip, { theme: "inverse", content: t('orders:planning_manufacturer_capacity_days_tooltip', {
                                            days: manufacturerCapacityDaysLabel,
                                        }), position: "top", delay: 200 },
                                        React.createElement("span", { className: styles.infoTrigger },
                                            React.createElement(InfoIcon, { className: styles.infoIcon }))))) }),
                            React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_non_working_days'), value: nonWorkingDaysInRange })),
                        React.createElement("div", { className: styles.calendarPreview },
                            React.createElement("div", { className: styles.calendarLabel }, t('orders:planning_manufacturer_calendar')),
                            React.createElement(CalendarRange, { readOnly: true, initialMonth: calendarInitialMonth, disabledDates: disabledDates, occupiedDates: occupiedDates, productionDates: productionDates, occupiedDateOrderIds: occupiedOrderIdsByDate, occupiedDateOrders: occupiedOrdersByDate, legend: {
                                    nonWorking: t('orders:planning_calendar_non_working_legend'),
                                    occupied: t('orders:planning_calendar_occupied_legend'),
                                    production: isPreviewMode
                                        ? t('orders:planning_calendar_production_legend')
                                        : undefined,
                                }, onVisibleRangeChange: handleCalendarVisibleRangeChange }))))),
            React.createElement("div", null,
                React.createElement(Header, { size: "s" }, t('orders:planning_production')),
                React.createElement(Grid, { columns: 1, gap: 12 },
                    React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: t('orders:planning_production_start_date'), value: productionStartDate }),
                    React.createElement(ParameterDetails, { size: "s", direction: "horizontal", label: isPreviewMode
                            ? t('orders:planning_production_end_date')
                            : t('orders:planning_virtual_production_end_date'), value: endDate }))),
            !isPreviewMode && (React.createElement(Grid, { columns: 2, gap: 12 },
                React.createElement("div", null),
                React.createElement(Button, { color: "primary", loading: status.approveOrderSpecificationProduction === 'loading', onClick: handleApproveProduction }, t('orders:planning_approve_production')))),
            isPreviewMode && (React.createElement(Grid, { columns: 2, gap: 12 },
                React.createElement("div", null),
                React.createElement(Button, { theme: "border", loading: status.revertOrderSpecificationProductionApproval === 'loading', onClick: handleReturnToPlanningMode }, t('orders:planning_return_to_planning_mode')))))));
}
export default memo(ProductionApprovalDetails);
